/**
 * Datagrid boot — replaces inline <script> blocks from Datagrid/template/*.latte.
 * Config comes from data-* on [data-nais-datagrid] / .nais-datagrid-filter-panel.
 */
import { hideBootstrapTooltips } from './bootstrap';

let datagridKnobThemeListenerBound = false;

declare global {
    interface Window {
        datagridRefreshTimers?: Record<string, ReturnType<typeof setTimeout>>;
        datagridRefreshCounters?: Record<string, ReturnType<typeof setInterval>>;
        datagridInitCallback?: (uid: string) => void;
        showFilter?: (element?: HTMLElement | JQuery | null) => void;
        resetCheckboxesBulkActions?: () => void;
        exportSelectedRows?: (elem: HTMLElement, paramPrefix: string) => boolean | void;
    }

    interface JQuery {
        contextMenu?: (settings: Record<string, unknown>) => JQuery;
        knob?: (options: Record<string, unknown>) => JQuery;
    }
}

/** Context menus for grid rows / head (from former Datagrid.latte javascript snippet). */
export function initDatagridContextMenus(uid: string): void {
    const $ = window.jQuery;
    if (!$?.fn || typeof ($ as JQuery & { contextMenu?: unknown }).fn !== 'object') {
        return;
    }
    if (typeof $.fn.contextMenu !== 'function') {
        return;
    }

    $(`#snippet-${uid}-rows tr.row-inner, #snippet-${uid}-rows div.tile, .divContextMenu`).each(function eachRow() {
        const el = this as HTMLElement;
        if (!el.id) {
            return;
        }
        $(el).contextMenu!({
            menuSelector: `#${el.id} .contextMenu`,
            otherMenu: '.contextMenu',
        });
    });

    $(`#snippet-${uid}-head, .divContextMenu`).each(function eachHead() {
        const el = this as HTMLElement;
        if (!el.id) {
            return;
        }
        $(el).contextMenu!({
            menuSelector: `#${el.id} .contextMenu`,
            otherMenu: '.contextMenu',
        });
    });
}

/** Auto-refresh timer (refreshDatagrid signal). */
export function initDatagridRefreshTimer(uid: string, intervalSec: number, refreshUrl: string): void {
    const $ = window.jQuery;
    if (!$?.nette || intervalSec <= 0 || !refreshUrl) {
        return;
    }

    window.datagridRefreshTimers = window.datagridRefreshTimers || {};
    if (window.datagridRefreshTimers[uid]) {
        clearTimeout(window.datagridRefreshTimers[uid]);
    }
    window.datagridRefreshTimers[uid] = setTimeout(() => {
        $.nette.ajax({ url: refreshUrl });
    }, intervalSec * 1000);
}

/** jQuery knob countdown next to the toolbar. */
export function initDatagridRefreshKnob(uid: string, intervalSec: number): void {
    const $ = window.jQuery;
    if (!$?.fn?.knob || intervalSec <= 0) {
        return;
    }

    const $knob = $(`#${uid}-knob`);
    if (!$knob.length) {
        return;
    }

    $knob.knob!({
        min: 0,
        max: intervalSec,
        step: 1,
        rotation: 'anticlockwise',
        font: '',
        lineCap: 'round',
        ...getDatagridRefreshKnobColors(),
        inputColor: 'var(--nais-text)',
        readOnly: true,
    });

    bindDatagridRefreshKnobThemeListener();

    let counter = intervalSec;
    window.datagridRefreshCounters = window.datagridRefreshCounters || {};
    if (window.datagridRefreshCounters[uid]) {
        clearInterval(window.datagridRefreshCounters[uid]);
    }
    window.datagridRefreshCounters[uid] = setInterval(() => {
        $knob.val(--counter).trigger('change');
        if (counter <= 0 && window.datagridRefreshCounters?.[uid]) {
            clearInterval(window.datagridRefreshCounters[uid]);
        }
    }, 1000);
}

function bindDatagridRefreshKnobThemeListener(): void {
    if (datagridKnobThemeListenerBound) {
        return;
    }

    document.addEventListener('nais:themechange', () => {
        const $ = window.jQuery;
        if (!$?.fn?.knob) {
            return;
        }

        $('.datagridCountdown input[id$="-knob"]').trigger('configure', getDatagridRefreshKnobColors());
    });
    datagridKnobThemeListenerBound = true;
}

function getDatagridRefreshKnobColors(): { fgColor: string; bgColor: string } {
    const styles = window.getComputedStyle(document.documentElement);

    return {
        fgColor: styles.getPropertyValue('--nais-link-color').trim() || '#3b82f6',
        bgColor: styles.getPropertyValue('--nais-border').trim() || '#dfe4ec',
    };
}

/** Rows-per-page select — was inline onchange in pagination.latte. */
export function bindDatagridRowsPerPageChange(): void {
    const $ = window.jQuery;
    if (!$?.nette) {
        return;
    }

    $(document)
        .off('change.naisDgRows', 'select.rowsOnPageSelect')
        .on('change.naisDgRows', 'select.rowsOnPageSelect', function onRowsChange() {
            const select = this as HTMLSelectElement;
            const option = select.options[select.selectedIndex];
            const href = option?.getAttribute('data-href');
            if (!href) {
                return;
            }
            $.nette.ajax({ type: 'get', url: href });
        });
}

/** Bulk cancel + export-selected — data-action instead of onclick. */
export function bindDatagridBulkActions(): void {
    const $ = window.jQuery;

    $(document)
        .off('click.naisDgBulk', '[data-action="datagrid-bulk-reset"]')
        .on('click.naisDgBulk', '[data-action="datagrid-bulk-reset"]', (event) => {
            event.preventDefault();
            if (typeof window.resetCheckboxesBulkActions === 'function') {
                window.resetCheckboxesBulkActions();
            }
        });

    $(document)
        .off('click.naisDgExport', '[data-action="datagrid-export-selected"]')
        .on('click.naisDgExport', '[data-action="datagrid-export-selected"]', function onExport(event) {
            const el = this as HTMLAnchorElement;
            const prefix = el.getAttribute('data-param-prefix') || '';
            if (typeof window.exportSelectedRows === 'function') {
                const result = window.exportSelectedRows(el, prefix);
                if (result === false) {
                    event.preventDefault();
                }
                return result;
            }
            return undefined;
        });
}

/** Filter panel boot — replaces filter.latte <script>. */
export function initDatagridFilterPanel(root: ParentNode | Document = document): void {
    const $ = window.jQuery;
    if (!$) {
        return;
    }

    const panels = root.querySelectorAll<HTMLElement>('.nais-datagrid-filter-panel[data-nais-datagrid-filter]');
    panels.forEach((panel) => {
        if (panel.dataset.naisFilterBooted === '1') {
            // Still re-run open/selects after AJAX replace — panel is new DOM each time.
        }
        panel.dataset.naisFilterBooted = '1';

        const shortUrl = panel.dataset.shortUrl || '';
        const openFilter = panel.dataset.filterOpen === '1';
        const showFastFilters = panel.dataset.showFastFilters === '1';
        const filterNameId = panel.dataset.filterNameId || '';

        if (window.ClipboardJS) {
            // eslint-disable-next-line no-new
            new window.ClipboardJS('#copyFilterUrlToClipboard');
        }

        if (openFilter || window.location.hash === '#filterOpen') {
            window.showFilter?.();
        }

        if (window.getComputedStyle(panel).display !== 'none') {
            window.init_datagrid_filter_selects?.(panel);
            window.initRangeLinks?.(panel);
        }

        // Radios are native — global initNativeChecks() usually already ran, this covers
        // panels that were still display:none (hidden filter body) at that point.
        window.initNativeChecks?.(panel);

        if (showFastFilters && filterNameId) {
            $(document)
                .off('keypress.saveFilter', `#${filterNameId}`)
                .on('keypress.saveFilter', `#${filterNameId}`, (e) => {
                    if (e.which === 13) {
                        $('.saveFilterButton').trigger('click');
                        e.preventDefault();
                        return false;
                    }
                    return undefined;
                });
        }

        // Bind once per document for copy/success/shorten/submit validation
        $(document)
            .off('click.naisDgCopyUrl', '#copyFilterUrlToClipboard')
            .on('click.naisDgCopyUrl', '#copyFilterUrlToClipboard', () => {
                const $ok = $('#copySuccess') as JQuery & {
                    fadeIn: (ms: number) => JQuery;
                    delay: (ms: number) => JQuery;
                    fadeOut: (ms: number) => JQuery;
                };
                $ok.fadeIn(1000).delay(1500).fadeOut(1000);
            });

        $(document)
            .off('click.naisDgFilterSubmit', '.resetFilterOpen')
            .on('click.naisDgFilterSubmit', '.resetFilterOpen', () => {
                if (window.location.hash === '#filterOpen') {
                    window.location.hash = '';
                }
                // Default submit proceeds; from≤to is enforced by initRangeLinks.
            });

        if (shortUrl) {
            $(document)
                .off('click.naisDgShortUrl', '#shortenFilterUrl')
                .on('click.naisDgShortUrl', '#shortenFilterUrl', function onShorten(event) {
                    event.preventDefault();
                    const $btn = $(this);
                    $.ajax({
                        url: shortUrl,
                        data: {
                            originalUrl: $('#copyFilterUrlToClipboard').attr('data-clipboard-text'),
                        },
                        type: 'post',
                        dataType: 'json',
                    }).done((response: { status?: string; shortUrl?: string; error?: string }) => {
                        if (response.status === 'success' && response.shortUrl) {
                            $('#copyFilterUrlToClipboard').attr('data-clipboard-text', response.shortUrl);
                            $('#filterUrl').text(response.shortUrl);
                            $btn.remove();
                        } else if (response.status === 'error' && window.PNotify) {
                            const notice = new window.PNotify({
                                animation: 'fade',
                                text: response.error,
                                delay: 7000,
                                mouse_reset: true,
                                type: 'error',
                                overlayClose: true,
                                buttons: { closer: false, sticker: false },
                            });
                            notice.get().click(() => notice.remove());
                        }
                    });
                });
        }
    });
}

/** Boot every [data-nais-datagrid] on the page (afterAjax / first load). */
export function initDatagridBoot(root: ParentNode | Document = document): void {
    const nodes = root.querySelectorAll<HTMLElement>('[data-nais-datagrid]');
    nodes.forEach((node) => {
        const uid = node.dataset.datagridUid || '';
        if (!uid) {
            return;
        }

        const interval = Number(node.dataset.refreshInterval || 0);
        const refreshUrl = node.dataset.refreshUrl || '';

        initDatagridContextMenus(uid);
        if (interval > 0) {
            initDatagridRefreshTimer(uid, interval, refreshUrl);
            initDatagridRefreshKnob(uid, interval);
        }

        if (typeof window.datagridInitCallback === 'function') {
            window.datagridInitCallback(uid);
        }
    });

    initDatagridFilterPanel(root);
    initDatagridMultipleSelect(root);
    hideBootstrapTooltips();
}

/** Fancybox “choose” close — was inline onClick. */
export function bindDatagridFancyboxClose(): void {
    const $ = window.jQuery;
    $(document)
        .off('click.naisDgFancyClose', '[data-action="datagrid-fancybox-close"]')
        .on('click.naisDgFancyClose', '[data-action="datagrid-fancybox-close"]', () => {
            try {
                window.parent?.jQuery?.fancybox?.close?.();
            } catch {
                // ignore cross-origin / missing fancybox
            }
        });
}

/** Datagrid toolbar actions — data-action hooks instead of inline onclick. */
export function bindDatagridToolbarActions(): void {
    const $ = window.jQuery;

    $(document)
        .off('click.naisDgShowSelected', '[data-action="datagrid-show-selected-entities"]')
        .on('click.naisDgShowSelected', '[data-action="datagrid-show-selected-entities"]', function onSelected(
            event
        ) {
            event.preventDefault();

            let multipleSelectName: string | null | undefined;
            try {
                multipleSelectName = window.parent?.activeMultipleSelect;
            } catch {
                multipleSelectName = window.activeMultipleSelect;
            }

            window.showOnlySelectedMultipleEntities?.(this, multipleSelectName);
        });

    $(document)
        .off('click.naisDgQuickFilters', '[data-action="datagrid-toggle-quick-filters"]')
        .on('click.naisDgQuickFilters', '[data-action="datagrid-toggle-quick-filters"]', () => {
            window.dropdown?.();
        });

    $(document)
        .off('click.naisDgOrderingToggle', '[data-action="datagrid-show-ordering"]')
        .on('click.naisDgOrderingToggle', '[data-action="datagrid-show-ordering"]', (event) => {
            event.preventDefault();
            window.showOrdering?.();
        });

    $(document)
        .off('click.naisDgFilterToggle', '[data-action="datagrid-show-filter"]')
        .on('click.naisDgFilterToggle', '[data-action="datagrid-show-filter"]', function onFilter(event) {
            event.preventDefault();
            window.showFilter?.(this);
        });

    $(document)
        .off('click.naisDgHideTooltip', '[data-action="datagrid-hide-tooltip"]')
        .on('click.naisDgHideTooltip', '[data-action="datagrid-hide-tooltip"]', function onHideTooltip() {
            hideBootstrapTooltips(this);
        });
}

/** Entity multi-select popup (former multiple_select.latte inline script). */
export function initDatagridMultipleSelect(root: ParentNode | Document = document): void {
    const $ = window.jQuery;
    if (!$ || !root.querySelector('[data-nais-multiple-select]')) {
        return;
    }

    const path = window.top?.location?.pathname || window.location.pathname;
    let list: Record<string, unknown> = {};
    try {
        const raw = localStorage.getItem(`currentSelections${path}`);
        list = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    } catch {
        list = {};
    }

    if (list && Object.keys(list).length) {
        $('input.multiselect').each(function eachMulti() {
            const id = $(this).attr('entityId');
            if (id && Object.prototype.hasOwnProperty.call(list, id)) {
                $(this).attr('checked', 'checked');
            }
        });
    }

    const $multi = $('.multiselect');

    $multi.off('change.naisDgMulti').on('change.naisDgMulti', function onChange() {
        const parentWin = window.parent as Window & {
            saveSelectedEntity?: (id: string, name: string) => void;
            removeSelectedEntity?: (id: string) => void;
        };
        if (this.checked) {
            parentWin.saveSelectedEntity?.($(this).attr('entityId') || '', $(this).attr('entityName') || '');
        } else {
            parentWin.removeSelectedEntity?.($(this).attr('entityId') || '');
        }
    });

    $('#multiselect-all')
        .off('change.naisDgMulti')
        .on('change.naisDgMulti', function onSelectAllChange() {
            const checked = this.checked;
            $('input.multiselect').each(function eachRow() {
                window.setNativeChecked?.(this, checked);
            });
        });
}

export function bindDatagridDelegates(): void {
    bindDatagridRowsPerPageChange();
    bindDatagridBulkActions();
    bindDatagridFancyboxClose();
    bindDatagridToolbarActions();
}
